import type { ProductStatus } from "@unbox/api";
import { Globe05, InfoCircle, ShieldZap, ShoppingBag01, UserSquare, VideoRecorder } from "@untitledui/icons";
import type { PropsWithChildren } from "react";
import { useAssuranceFilters } from "@/api/assurance/assurance-filters";
import { useVideoContentFeatures } from "@/api/content-features";
import { Button } from "@/components/base/buttons/button";
import { CloseButton } from "@/components/base/buttons/close-button";
import { Input } from "@/components/base/input/input";
import { Label } from "@/components/base/input/label";
import {
  ContentFeatureFilter,
  CreatorFilter,
  CreatorListFilter,
  ProductFilter,
} from "@/components/filters/extra-filters";
import { PillField } from "@/components/filters/pill-field";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Form, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { useIsAssurance } from "@/hooks/use-is-assurance";
import { ACTION_TYPES, type ActionType, getLogActionLabel } from "@/lib/assurance/action-types";
import { PRODUCT_STATUS_LABELS, PRODUCT_STATUSES } from "@/lib/products";
import { useFilters } from "@/providers/filter-provider";
import { cx } from "@/utils/cx";
import { toTitleCase } from "@/utils/toTitleCase";
import { Tooltip, TooltipTrigger } from "../base/tooltip/tooltip";

type FilterSection = "safety-risks" | "featured-products" | "creator-type" | "video-details";
type FilterPanelProps = {
  open: boolean;
  close: () => void;
  clearAll?: () => void;
  openSection?: FilterSection;
} & PropsWithChildren;

export const FilterPanelLegacy = ({ open, close, clearAll, openSection }: FilterPanelProps) => {
  const { reset, form } = useFilters();
  const formValues = form.watch();

  // Check if any filter has a non-default value
  const hasActiveFilters = Object.entries(formValues).some(([_key, value]) => {
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "string") return value.trim() !== "";
    return value !== undefined && value !== null;
  });

  const handleClear = () => {
    clearAll?.();
    reset();
  };

  return (
    <div
      className={cx(
        "fixed right-0 w-96 bg-secondary border-l border-gray-200 z-50",
        "transform transition-transform duration-300 ease-in-out",
        open ? "translate-x-0" : "translate-x-full",
      )}
      style={{
        top: "var(--sidebar-top-offset, 0px)",
        height: "calc(100vh - var(--sidebar-top-offset, 0px))",
      }}
    >
      <div className="flex flex-col h-full p-6">
        <div className="flex items-center justify-between mb-6">
          <Button className="px-0" color="tertiary" size="sm" onClick={handleClear} isDisabled={!hasActiveFilters}>
            Clear all
          </Button>

          <div className="flex flex-row items-center">
            <Button className="pr-0" color="tertiary" size="sm" onClick={close}>
              Close
            </Button>
            <CloseButton className="px-0 mr-[-10px]" onClick={close} />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          <Filters openSection={openSection} />
        </div>
      </div>
    </div>
  );
};

const Filters = ({ openSection: _openSection }: { openSection?: FilterSection }) => {
  const { form, isActive } = useFilters();
  const { data } = useAssuranceFilters();
  const { data: features } = useVideoContentFeatures();
  const isAssurance = useIsAssurance();
  return (
    <Form {...form}>
      <div className="space-y-8">
        {isActive("minGmv") && isActive("maxGmv") && (
          <div className="flex flex-col gap-1">
            <Label className="text-sm">GMV Range</Label>
            <div className="flex flex-row gap-2">
              <FormField
                control={form.control}
                name="minGmv"
                render={({ field }) => (
                  <Input inputClassName="text-sm" type="number" placeholder="Min GMV" {...field} />
                )}
              />
              <FormField
                control={form.control}
                name="maxGmv"
                render={({ field }) => (
                  <Input inputClassName="text-sm" type="number" placeholder="Max GMV" {...field} />
                )}
              />
            </div>
          </div>
        )}
        {isActive("minFollowers") && isActive("maxFollowers") && (
          <div className="flex flex-col gap-1">
            <Label className="text-sm">Followers Range</Label>
            <div className="flex flex-row gap-2">
              <FormField
                control={form.control}
                name="minFollowers"
                render={({ field }) => (
                  <Input inputClassName="text-sm" type="number" placeholder="Min Followers" {...field} />
                )}
              />
              <FormField
                control={form.control}
                name="maxFollowers"
                render={({ field }) => (
                  <Input inputClassName="text-sm" type="number" placeholder="Max Followers" {...field} />
                )}
              />
            </div>
          </div>
        )}
        {isActive("minUnitsSold") && isActive("maxUnitsSold") && (
          <div className="flex flex-col gap-1">
            <Label className="text-sm">Units Sold Range</Label>
            <div className="flex flex-row gap-2">
              <FormField
                control={form.control}
                name="minUnitsSold"
                render={({ field }) => (
                  <Input inputClassName="text-sm" type="number" placeholder="Min Units Sold" {...field} />
                )}
              />
              <FormField
                control={form.control}
                name="maxUnitsSold"
                render={({ field }) => (
                  <Input inputClassName="text-sm" type="number" placeholder="Max Units Sold" {...field} />
                )}
              />
            </div>
          </div>
        )}
        {isActive("minRating") && isActive("maxRating") && (
          <div className="flex flex-col gap-1">
            <Label className="text-sm">Seller Rating</Label>
            <div className="flex flex-row gap-2">
              <FormField
                control={form.control}
                name="minRating"
                render={({ field }) => (
                  <Input inputClassName="text-sm" type="number" placeholder="Min Rating" {...field} />
                )}
              />
              <FormField
                control={form.control}
                name="maxRating"
                render={({ field }) => (
                  <Input inputClassName="text-sm" type="number" placeholder="Max Rating" {...field} />
                )}
              />
            </div>
          </div>
        )}
        <Accordion type="single" collapsible className="w-full" defaultValue={""}>
          {(isActive("gender") || isActive("age")) && (
            <AccordionItem value="audience">
              <AccordionTrigger>
                <div className="text-sm flex flex-row gap-2">
                  <Globe05 className="w-4 h-4" /> Audience
                </div>
              </AccordionTrigger>
              <AccordionContent className="flex flex-col gap-4 text-balance">
                {isActive("gender") && (
                  <div className="flex flex-col gap-1">
                    <Label className="text-sm">
                      Gender
                      <Tooltip title="Gender making up >50% of audience">
                        <TooltipTrigger className="ml-1 cursor-pointer transition duration-100 ease-linear hover:text-fg-quaternary_hover focus:text-fg-quaternary_hover">
                          <InfoCircle className="w-4 h-4" />
                        </TooltipTrigger>
                      </Tooltip>
                    </Label>
                    <FormField
                      control={form.control}
                      name="gender"
                      render={({ field }) => (
                        <div className="flex flex-row bg-primary rounded p-1">
                          <Button
                            className={cx(
                              "flex flex-row w-full text-sm justify-center p-2 border-1 border-white",
                              field.value === undefined && "border-foreground",
                            )}
                            onClick={() => field.onChange(undefined)}
                            id="all"
                            color="tertiary"
                          >
                            All
                          </Button>
                          <Button
                            className={cx(
                              "flex flex-row w-full text-sm justify-center p-2 border-1 border-white",
                              field.value === "male" && "border-foreground",
                            )}
                            onClick={() => field.onChange("male")}
                            id="male"
                            color="tertiary"
                          >
                            Male
                          </Button>
                          <Button
                            className={cx(
                              "flex flex-row w-full text-sm justify-center p-2 border-1 border-white",
                              field.value === "female" && "border-foreground",
                            )}
                            onClick={() => field.onChange("female")}
                            id="female"
                            color="tertiary"
                          >
                            Female
                          </Button>
                        </div>
                      )}
                    />
                  </div>
                )}
                {isActive("age") && (
                  <div className="flex flex-col gap-2">
                    <Label className="text-sm text-gray-700">
                      Age
                      <Tooltip title="Age group making up >30% of audience">
                        <TooltipTrigger className="ml-1 cursor-pointer transition duration-100 ease-linear hover:text-fg-quaternary_hover focus:text-fg-quaternary_hover">
                          <InfoCircle className="w-4 h-4" />
                        </TooltipTrigger>
                      </Tooltip>
                    </Label>
                    <FormField
                      control={form.control}
                      name="age"
                      render={({ field }) => (
                        <PillField
                          value={field.value}
                          options={["18-24", "25-34", "35-44", "45-54", "55+"]}
                          onChange={field.onChange}
                        />
                      )}
                    />
                  </div>
                )}
              </AccordionContent>
            </AccordionItem>
          )}
          {(isActive("creatorType") || isActive("creator") || isActive("creatorList")) && (
            <AccordionItem value="creator-type">
              <AccordionTrigger>
                <div className="text-sm flex flex-row gap-2">
                  <UserSquare className="w-4 h-4" /> Creators
                </div>
              </AccordionTrigger>
              <AccordionContent className="flex flex-col gap-4 text-balance">
                {isActive("creatorType") && (
                  <FormField
                    control={form.control}
                    name="creatorType"
                    render={({ field }) => (
                      <>
                        <Label className="text-sm">Content Owner</Label>
                        <div className="flex flex-row bg-primary rounded p-1 ">
                          <Button
                            className={cx(
                              "flex flex-row w-full text-sm justify-center p-2  border-1  border-white",
                              field.value === undefined && "border-foreground",
                            )}
                            onClick={() => field.onChange(undefined)}
                            id="all"
                            color="tertiary"
                          >
                            All
                          </Button>
                          <Button
                            className={cx(
                              "flex flex-row w-full text-sm justify-center p-2  border-1  border-white",
                              field.value === "affiliate" && "border-foreground",
                            )}
                            onClick={() => field.onChange("affiliate")}
                            id="affiliate"
                            color="tertiary"
                          >
                            Affiliate
                          </Button>
                          <Button
                            className={cx(
                              "flex flex-row w-full text-sm justify-center p-2  border-1  border-white",
                              field.value === "brand" && "border-foreground",
                            )}
                            onClick={() => field.onChange("brand")}
                            id="brand"
                            color="tertiary"
                          >
                            Brand
                          </Button>
                        </div>
                      </>
                    )}
                  />
                )}
                {isActive("creator") && (
                  <FormField
                    control={form.control}
                    name="creator"
                    render={({ field }) => <CreatorFilter field={field} />}
                  />
                )}
                {isActive("creatorList") && (
                  <FormField
                    control={form.control}
                    name="creatorList"
                    render={({ field }) => <CreatorListFilter field={field} />}
                  />
                )}
              </AccordionContent>
            </AccordionItem>
          )}
          {isActive("hooks") &&
            isActive("sellingPoints") &&
            isActive("videoStyles") &&
            isActive("tactics") && ( // Package deal
              <AccordionItem value="video-details">
                <AccordionTrigger>
                  <div className="text-sm flex flex-row gap-2">
                    <VideoRecorder className="w-4 h-4" /> Video Details
                  </div>
                </AccordionTrigger>
                <AccordionContent className="flex flex-col gap-4 text-balance">
                  <FormField
                    control={form.control}
                    name="hooks"
                    render={({ field }) => (
                      <ContentFeatureFilter
                        title="Hooks"
                        field={field}
                        options={features?.hooks.map((f) => ({ id: f, label: f })) ?? []}
                      />
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="tactics"
                    render={({ field }) => (
                      <ContentFeatureFilter
                        title="Tactics"
                        field={field}
                        options={
                          features?.tactics.map((f) => ({ id: f, label: toTitleCase(f.replace("_", " ")) })) ?? []
                        }
                      />
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="sellingPoints"
                    render={({ field }) => (
                      <ContentFeatureFilter
                        title="Selling Points"
                        field={field}
                        options={features?.sellingPoints.map((f) => ({ id: f, label: f })) ?? []}
                      />
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="videoStyles"
                    render={({ field }) => (
                      <ContentFeatureFilter
                        title="Video Styles"
                        field={field}
                        options={features?.videoStyles.map((f) => ({ id: f, label: f })) ?? []}
                      />
                    )}
                  />
                </AccordionContent>
              </AccordionItem>
            )}
          {(isActive("product") || isActive("productStatus")) && (
            <AccordionItem value="featured-products">
              <AccordionTrigger>
                <div className="text-sm flex flex-row gap-2">
                  <ShoppingBag01 className="w-4 h-4" /> Featured Products
                </div>
              </AccordionTrigger>
              <AccordionContent className="flex flex-col gap-4 text-balance">
                <div className="flex flex-col gap-1">
                  {isActive("product") && (
                    <FormField
                      control={form.control}
                      name="product"
                      render={({ field }) => <ProductFilter field={field} />}
                    />
                  )}
                  {isActive("productStatus") && (
                    <FormItem className="flex flex-col">
                      <FormLabel className="text-sm text-gray-700">Product Status</FormLabel>
                      <FormField
                        control={form.control}
                        name="productStatus"
                        render={({ field }) => (
                          <PillField
                            value={field.value}
                            options={PRODUCT_STATUSES}
                            onChange={field.onChange}
                            getLabel={(option) => {
                              return PRODUCT_STATUS_LABELS[option as ProductStatus] ?? "Unknown";
                            }}
                          />
                        )}
                      />
                    </FormItem>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          )}
          {isAssurance && (isActive("riskId") || isActive("riskTier") || isActive("actionTaken")) && (
            <AccordionItem value="safety-risks">
              <AccordionTrigger>
                <div className="text-sm flex flex-row gap-2">
                  <ShieldZap className="w-4 h-4" /> Safety Risks
                </div>
              </AccordionTrigger>
              <AccordionContent className="flex flex-col gap-4 text-balance">
                {isActive("riskTier") && (
                  <FormField
                    control={form.control}
                    name="riskTier"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel className="text-sm text-gray-700">Risk Tier</FormLabel>

                        <PillField
                          value={field.value}
                          options={["1", "2"]}
                          onChange={field.onChange}
                          getLabel={(val) => `Level ${val}`}
                        />
                      </FormItem>
                    )}
                  />
                )}
                {isActive("riskId") && (
                  <FormField
                    control={form.control}
                    name="riskId"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel className="text-sm text-gray-700">Risk Type</FormLabel>

                        <PillField
                          value={field.value}
                          options={data?.riskTypes?.map((r) => r.id) ?? []}
                          onChange={field.onChange}
                          getLabel={(id) => data?.riskTypes?.find((r) => r.id === id)?.label ?? id}
                        />
                      </FormItem>
                    )}
                  />
                )}
                {isActive("actionTaken") && (
                  <FormField
                    control={form.control}
                    name="actionTaken"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel className="text-sm text-gray-700">Action Type</FormLabel>

                        <PillField
                          value={field.value}
                          options={ACTION_TYPES as unknown as string[]}
                          onChange={field.onChange}
                          getLabel={(val) => getLogActionLabel(val as ActionType)}
                        />
                      </FormItem>
                    )}
                  />
                )}
              </AccordionContent>
            </AccordionItem>
          )}
        </Accordion>
      </div>
    </Form>
  );
};
